import {
    Dispatch,
    SetStateAction,
    useContext,
    useState,
    useCallback,
    useRef,
} from "react"

import {
    ApplicationContext,
    IDraftSubmissionData,
} from "../../../../components/submitApplication/single/applicationProvider"
import { BELFAST_POST_CODE_AREA } from "../../../../constants/generalConstants"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { useAutoSave } from "../../../../hooks/useAutoSave"
import { useGetUserData } from "../../../../hooks/useGetUserData"
import { postClientProtected } from "../../clients/clients"
import { SUBMIT_DRAFT_FORM } from "../../urls/hybrid"

// Define an interface for the API response
interface DraftAPIResponse {
    draftId?: string
    [key: string]: any // Allow other properties in the response
}

interface ISubmitDraftApplication {
    isSubmitting: boolean
    setIsSubmitting: Dispatch<SetStateAction<boolean>>
    error: IAppError | undefined
    postDraftApplication: (
        isFirstSubmission?: boolean,
        currentFormData?: Record<string, any>,
        providedIndex?: number
    ) => Promise<void>
    hasUnsavedChanges: boolean
    hasBasicDetailsComplete: boolean
    triggerSaveNow: () => Promise<void> // Added to give forms direct access to trigger saves
    cancelAutoSave: () => void // Added to give forms ability to cancel auto-save
}

/**
 * Hook to handle the submission of commissioning form by an installer
 * Auto-saves draft every 30 seconds once basic details are entered
 * Auto-save will stop after 5 minutes to prevent indefinite triggering
 * @returns {ISubmitDraftApplication}
 */
const useSubmitDraft = (): ISubmitDraftApplication => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [hasBasicDetailsComplete, setHasBasicDetailsComplete] =
        useState(false)

    // Reference to track if a save is in progress (for manual submissions)
    const saveInProgressRef = useRef<boolean>(false)
    const lastSavedDraftIdRef = useRef<string | undefined>(undefined)

    const { username } = useGetUserData()

    const getJurisdictionFromPostcode = (postcode?: any): "GB" | "NI" =>
        postcode?.toLowerCase().startsWith(BELFAST_POST_CODE_AREA) ? "NI" : "GB"

    const {
        formData,
        fixedInputs,
        jurisdiction,
        supplementingInfo,
        draftId,
        setDraftId,
        currentStepIndex,
        stepLabels,
        draftData,
        setDraftData,
        isAmendmentMode,
        defaultCompletionStatus,
    } = useContext(ApplicationContext)

    // Use useCallback to memoize the postDraftApplication function
    const postDraftApplication = useCallback(
        async (
            isFirstSubmission?: boolean,
            currentFormData?: Record<string, any>,
            providedIndex?: number
        ): Promise<void> => {
            if (isAmendmentMode) {
                return
            }
            // Prevent duplicate submissions
            if (
                isSubmitting ||
                (!isFirstSubmission && saveInProgressRef.current)
            ) {
                return
            }

            saveInProgressRef.current = true
            setIsSubmitting(true)

            try {
                const safeFixedInputs = fixedInputs || {}
                let lctsArray: string[] = []

                // Only try to access lct if safeFixedInputs exists
                if (safeFixedInputs && "lcts" in safeFixedInputs) {
                    const lctValue = safeFixedInputs.lcts
                    if (Array.isArray(lctValue)) {
                        lctsArray = lctValue as string[]
                    } else if (lctValue) {
                        lctsArray = [lctValue as string]
                    }
                }

                // Merge current form data with context's form data if provided
                const mergedFormData = {
                    ...formData,
                    ...(currentFormData || {}),
                }

                // Convert attachments data to the correct format - ensure consistent type
                let attachments: Record<string, unknown>[] = []
                if (Array.isArray(mergedFormData.additionalAttachments)) {
                    attachments = mergedFormData.additionalAttachments.map(
                        (item) =>
                            typeof item === "string"
                                ? { value: item }
                                : (item as Record<string, unknown>)
                    )
                } else if (mergedFormData.additionalAttachments) {
                    // Convert single item to an array with one element
                    const item = mergedFormData.additionalAttachments
                    attachments = [
                        typeof item === "string"
                            ? { value: item }
                            : (item as Record<string, unknown>),
                    ]
                }

                const completionStatus = providedIndex || currentStepIndex

                let latestCompletionStatus = Math.round(
                    (completionStatus / (stepLabels?.length || 1)) * 100
                )

                if (latestCompletionStatus < defaultCompletionStatus) {
                    latestCompletionStatus = defaultCompletionStatus
                }

                const custJuristriction = getJurisdictionFromPostcode(
                    mergedFormData?.installerCustomerDetails
                        .installationPostCode
                )

                // Create submission with defensive programming
                const formDataForSubmission: IDraftSubmissionData = {
                    installerUserName: username || "",
                    formData: { ...mergedFormData },
                    fixedInputs: safeFixedInputs,
                    jurisdiction: custJuristriction,
                    lcts: lctsArray,
                    supplementingInfo: mergedFormData.supplementingInfo,
                    supplyDetails: mergedFormData.supplyDetailsMerged,
                    additionalAttachments: attachments,
                    devicesToInstall: mergedFormData.devicesToInstall,
                    newPremisesMaxDemand:
                        Number(mergedFormData.newPremisesMaxDemand) || 0,
                    completionStatus: latestCompletionStatus,
                    ...(draftId ? { draftId } : {}),
                }

                // Check if data has changed from what's in draftData
                if (
                    JSON.stringify(formDataForSubmission) !==
                    JSON.stringify(draftData)
                ) {
                    const res = (await postClientProtected(
                        SUBMIT_DRAFT_FORM,
                        formDataForSubmission
                    )) as DraftAPIResponse

                    if (res?.draftId) {
                        const newDraftId = res.draftId
                        // Store last saved draft ID to prevent multiple draft creations
                        lastSavedDraftIdRef.current = newDraftId

                        // Only update the context draftId if it's different
                        if (draftId !== newDraftId) {
                            setDraftId(newDraftId)
                        }
                    }
                    // Set state on successfully saved
                    setHasBasicDetailsComplete(true)
                    setDraftData(formDataForSubmission)
                    setHasUnsavedChanges(false)
                    setError(undefined)
                } else {
                    setHasUnsavedChanges(false)
                }
            } catch (err) {
                console.error("Error saving draft:", err)
                const errorObj = parseErrorAsObj(err)
                setError(errorObj)
            } finally {
                setIsSubmitting(false)
                saveInProgressRef.current = false
            }
        },
        [
            formData,
            draftData,
            fixedInputs,
            jurisdiction,
            supplementingInfo,
            draftId,
            setDraftId,
            setDraftData,
            isSubmitting,
            currentStepIndex,
            stepLabels,
            isAmendmentMode,
        ]
    )
    // Trigger autosave every 30 seconds
    const { triggerSaveNow, cancelAutoSave } = useAutoSave({
        interval: 30000, // 30 seconds
        maxDuration: 300000, // 5 minutes (300,000ms)
        disabled: isAmendmentMode, // Disable in amendment mode
        onSave: () => postDraftApplication(false),
        condition: !!draftId, // Only auto-save if we have a draftId
    })

    return {
        isSubmitting,
        setIsSubmitting,
        error,
        postDraftApplication,
        hasUnsavedChanges,
        hasBasicDetailsComplete,
        triggerSaveNow, // Expose the manual save trigger
        cancelAutoSave, // Expose the ability to cancel auto-save
    }
}

export default useSubmitDraft
